import {
  Alert,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from 'recharts';

import { readContentHealth } from '../api/endpoints';
import type { ChallengeHealth, ChallengeHealthRow } from '../api/types';
import { ChartCard } from '../components/ChartCard';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { sortRows, useSort } from '../components/useSort';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { useChartTheme } from '../components/useChartTheme';
import { CHART_SERIES } from '../theme/palette';

const PERIODS = [7, 30, 90];

/**
 * Below this, a challenge is worth looking at. It is a prompt to read the task, not a verdict — a genuinely
 * hard challenge sitting at the top of its level range will live down here and be working exactly as
 * intended.
 */
const LOW_ACCURACY = 0.4;

/** Columns this table can be ordered by. Sorted in the browser — the whole list is already here. */
type SortKey =
  | 'challengeCode'
  | 'skillCode'
  | 'issued'
  | 'closed'
  | 'accuracy'
  | 'averageAttempts'
  | 'averageHints'
  | 'averageLevel'
  | 'expired';

export const ContentHealthPage = () => {
  const chartTheme = useChartTheme();
  const [days, setDays] = useState<number>(30);
  const [skill, setSkill] = useState('');
  const sort = useSort<SortKey>('challengeCode');
  const { data, error, isLoading, refetch } = useQuery<ChallengeHealth>({
    queryKey: ['content-health', days],
    queryFn: () => readContentHealth(days),
  });

  const all = data?.challenges ?? [];
  const struggling = all.filter((row) => row.closed >= 5 && row.accuracy < LOW_ACCURACY);
  // Sorted and filtered **here**, because this table holds its whole list: there is no page 2 whose rows
  // this could be getting wrong (R0141).
  const VALUE: Record<SortKey, (row: ChallengeHealthRow) => string | number | null> = {
    challengeCode: (row) => row.challengeCode,
    skillCode: (row) => row.skillCode,
    issued: (row) => row.issued,
    closed: (row) => row.closed,
    accuracy: (row) => row.accuracy,
    averageAttempts: (row) => row.averageAttempts,
    averageHints: (row) => row.averageHints,
    averageLevel: (row) => row.averageLevel,
    expired: (row) => row.expired,
  };
  const rows = sortRows(
    all.filter((row: ChallengeHealthRow) => skill === '' || row.skillCode === skill),
    sort.direction,
    VALUE[sort.key],
  );
  const skills = [...new Set(all.map((row: ChallengeHealthRow) => row.skillCode))].sort();

  return (
    <>
      <PageHeader
        title="Zdravie obsahu"
        subtitle="Ktoré úlohy sa deťom naozaj zadávajú, ako sa im darí ich riešiť a ktoré sa nezadali ani raz. Nízka úspešnosť je otázka na úlohu, nie na dieťa."
        action={
          <TextField
            select
            label="Obdobie"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            sx={{ minWidth: 176 }}
          >
            {PERIODS.map((option) => (
              <MenuItem key={option} value={option}>
                Posledných {option} dní
              </MenuItem>
            ))}
          </TextField>
        }
      />

      <ErrorNotice error={error} onRetry={() => void refetch()} />

      {data && data.neverServed.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {data.neverServed.length} typov úloh sa za obdobie nezadalo ani raz. Buď sú vypnuté, alebo sa k
            nim skladanie dennej sady nedostane.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {data.neverServed.map((code) => (
              <Chip key={code} label={code} size="small" variant="outlined" />
            ))}
          </Stack>
        </Alert>
      )}

      {struggling.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {struggling.length} typov úloh má úspešnosť pod {Math.round(LOW_ACCURACY * 100)} %. Stojí za to
          prečítať si zadanie — najčastejšie je problém v ňom, nie v náročnosti.
        </Alert>
      )}

      {data && data.errorTypes.length > 0 && (
        <ChartCard
          title="Najčastejšie typy chýb"
          description="Naprieč celou platformou a bez väzby na konkrétne dieťa. V súhrne to hovorí, ktorú úlohu prepísať; pri jednom dieťati by to bola diagnóza, ktorú produkt zámerne nerobí."
        >
          <BarChart
            data={data.errorTypes}
            layout="vertical"
            margin={{ top: 8, right: 16, bottom: 8, left: 96 }}
          >
            <CartesianGrid {...chartTheme.grid} horizontal={false} />
            <XAxis type="number" allowDecimals={false} {...chartTheme.axis} />
            <YAxis type="category" dataKey="errorType" width={150} {...chartTheme.axis} />
            <Tooltip {...chartTheme.tooltip} />
            <Bar dataKey="occurrences" name="Výskyty" radius={[0, 6, 6, 0]} maxBarSize={22}>
              {data.errorTypes.map((entry, index) => (
                <Cell key={entry.errorType} fill={CHART_SERIES[index % CHART_SERIES.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartCard>
      )}

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Úlohy za obdobie
          </Typography>

          <TableFilters>
            <TextField
              size="small"
              select
              label="Oblasť"
              value={skill}
              onChange={(event) => setSkill(event.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">všetky</MenuItem>
              {skills.map((one: string) => (
                <MenuItem key={one} value={one}>
                  {one}
                </MenuItem>
              ))}
            </TextField>
          </TableFilters>

          <TableContainer>
            <Table size="small" sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <SortableCell sort={sort} column="challengeCode">
                    Úloha
                  </SortableCell>
                  <SortableCell sort={sort} column="skillCode">
                    Oblasť
                  </SortableCell>
                  <SortableCell sort={sort} column="issued" align="right">
                    Zadané
                  </SortableCell>
                  <SortableCell sort={sort} column="closed" align="right">
                    Uzavreté
                  </SortableCell>
                  <SortableCell sort={sort} column="accuracy" align="right">
                    Úspešnosť
                  </SortableCell>
                  <SortableCell sort={sort} column="averageAttempts" align="right">
                    Pokusy
                  </SortableCell>
                  <SortableCell sort={sort} column="averageHints" align="right">
                    Nápovedy
                  </SortableCell>
                  <SortableCell sort={sort} column="averageLevel" align="right">
                    Úroveň
                  </SortableCell>
                  <SortableCell sort={sort} column="expired" align="right">
                    Vypršané
                  </SortableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && <TableSkeletonRows columns={9} />}
                {rows.map((row) => (
                  <ChallengeRow key={row.challengeCode} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!isLoading && rows.length === 0 && (
            <EmptyState
              title="Za toto obdobie sa nezadala žiadna úloha"
              detail="Buď sa aplikácia nepoužívala, alebo je obdobie príliš krátke."
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

const ChallengeRow = ({ row }: { row: ChallengeHealthRow }) => {
  const isStruggling = row.closed >= 5 && row.accuracy < LOW_ACCURACY;

  return (
    <TableRow hover>
      <TableCell sx={{ fontFamily: 'monospace' }}>{row.challengeCode}</TableCell>
      <TableCell>
        <Chip label={row.skillCode} size="small" variant="outlined" />
      </TableCell>
      <TableCell align="right">{row.issued}</TableCell>
      <TableCell align="right">{row.closed}</TableCell>
      <TableCell align="right" sx={{ minWidth: 132 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="body2" color={isStruggling ? 'warning.main' : 'text.primary'}>
            {row.closed === 0 ? '—' : `${Math.round(row.accuracy * 100)} %`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.round(row.accuracy * 100)}
            color={isStruggling ? 'warning' : 'primary'}
            sx={{ width: 56, height: 6, borderRadius: 3 }}
          />
        </Stack>
      </TableCell>
      <TableCell align="right">{row.averageAttempts}</TableCell>
      <TableCell align="right">{row.averageHints}</TableCell>
      <TableCell align="right">{row.averageLevel}</TableCell>
      <TableCell align="right">{row.expired}</TableCell>
    </TableRow>
  );
};
